import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { find, isNaN } from 'lodash-es';
import { Text } from '@chakra-ui/react';
import Board from '../../components/Board';
import List from '../../components/List';
import RulesRemarkModal from '../../components/RulesRemarkModal';
import TabContent from '../../components/TabContent';
import BlockTitle from '../../components/BlockTitle';
import dayjs from 'dayjs';
import memberCenterService from '../../../../service/memberCenterService';
import { useAppContext } from '../../context/appContext';
import { toMoney } from '../../../../utils/money';
import { hasFeature } from '../../../../utils/featureService';
import TableRenderComponent from './components/TableRenderComponent';
import { TAB_TYPE } from '../../constants';
import { getTabUrl } from '../../utils/tab';

const PAGE_LIMIT = 24;
const EARN_FROM_ORDER = 'earn_from_order';
const STATUS_ACTIVE = 'active';
const REDEEM_ORDER_RULES = ['max_percentage_per_order', 'max_amount_per_order'];

const Points = () => {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 0,
  });
  const [rules, setRules] = useState(null);
  const [pointEqualCash, setPointEqualCash] = useState('');
  const [isRedeemOrderActive, setIsRedeemOrderActive] = useState(false);
  const [summaryInfo, setSummaryInfo] = useState(null);
  const [rulesDescription, setRulesDescription] = useState('');
  const [isPromotionExist, setIsPromotionExist] = useState(false);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [ruleRemark, setRuleRemark] = useState('');
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { slFeatureService } = useAppContext();
  const hideCreditPointRecordBalance = hasFeature(
    'hide_credit_point_record_balance',
  );

  const { merchantData, currentUser } = window.mainConfig;
  const isMemberPointEnable =
    merchantData?.user_setting?.data?.enable_member_point;

  const fetchMemberPoints = useCallback(
    (page = 1) => {
      if (loading) {
        return;
      }
      setLoading(true);
      memberCenterService
        .fetchMemberPoints({
          limit: PAGE_LIMIT,
          page,
        })
        .then((res) => {
          if (!res?.data?.items) {
            throw new Error('Error fetchMemberPoints');
          }
          const { items = [], total } = res?.data || {};
          const newPoints = (points || []).concat(items);

          setPoints(newPoints);
          setPagination({
            page,
            pages: window.Math.ceil(total / PAGE_LIMIT),
          });
        }, [])
        .finally(() => {
          setLoading(false);
        });
    },
    [loading, points],
  );

  const fetchRules = useCallback(() => {
    memberCenterService.fetchMemberPointRules().then((res) => {
      setRules(res.data.items);
    });
  }, []);

  const fetchFulfillmentSummary = useCallback(() => {
    memberCenterService.fetchFulfillmentSummary(1).then((res) => {
      const { items, remaining_value, never_expire_item } = res.data;
      setSummaryInfo({
        items,
        remaining_value,
        never_expire_item,
      });
    });
  }, []);

  const shouldShowNextExpiryPoints = summaryInfo?.items?.length > 0;
  const shouldShowNeverExpirePoints =
    !shouldShowNextExpiryPoints &&
    summaryInfo?.never_expire_item?.total_value > 0;

  const earnFromOrderRuleArray = useMemo(
    () => (rules || []).filter((rule) => rule.rule_type === EARN_FROM_ORDER),
    [rules],
  );

  const earnFromOrderRule = useMemo(() => {
    const targetRule = earnFromOrderRuleArray.find((rule) => {
      if (
        rule.membership_tier_id === currentUser.membership_tier_id &&
        rule.status === STATUS_ACTIVE
      ) {
        return rule;
      }
    });
    return targetRule;
  }, [currentUser.membership_tier_id, earnFromOrderRuleArray]);

  const nullMembershipTierRule = useMemo(
    () =>
      earnFromOrderRuleArray.find((rule) => rule.membership_tier_id === null),
    [earnFromOrderRuleArray],
  );

  const redeemOrderRule = useMemo(
    () => find(rules, (rule) => REDEEM_ORDER_RULES.includes(rule.rule_type)),
    [rules],
  );

  const computedRuleRemark = useCallback(() => {
    const targetRule = earnFromOrderRule || nullMembershipTierRule;
    if (!targetRule) return;
    const ruleRemarks = targetRule.remarks_translations || {};
    setRuleRemark(ruleRemarks[language] || ruleRemarks['en'] || '');
  }, [earnFromOrderRule, language, nullMembershipTierRule]);

  const computedDataByRules = useCallback(() => {
    if (
      !slFeatureService?.hasFeature('point_redeem_to_cash') ||
      !isMemberPointEnable
    ) {
      return;
    }

    const updatedIsRedeemOrderActive =
      redeemOrderRule?.status === STATUS_ACTIVE;
    setIsRedeemOrderActive(updatedIsRedeemOrderActive);
    let discountMoney = '';
    try {
      discountMoney = toMoney({
        dollars: redeemOrderRule?.price_per_unit,
      }).label;
    } catch (e) {
      discountMoney = '';
    }
    if (updatedIsRedeemOrderActive && discountMoney) {
      setRulesDescription(
        t('member_points.redeem_cash_description', {
          costPoint: redeemOrderRule.unit_point,
          discountMoney,
        }),
      );
    }

    if (redeemOrderRule) {
      const pointEqualCashValue =
        window.parseInt(
          (currentUser.member_point_balance || 0) / redeemOrderRule.unit_point,
        ) * redeemOrderRule.price_per_unit;

      const pointEqualCashValueMoney = isNaN(pointEqualCashValue)
        ? ''
        : toMoney({
            dollars: pointEqualCashValue,
          }).label;
      setPointEqualCash(pointEqualCashValueMoney);
    }
  }, [
    currentUser.member_point_balance,
    isMemberPointEnable,
    redeemOrderRule,
    slFeatureService,
    t,
  ]);

  useEffect(() => {
    if ((rules || []).length === 0) {
      return;
    }

    computedRuleRemark();
    computedDataByRules();
  }, [computedDataByRules, computedRuleRemark, rules]);

  const checkRedeemGiftPromotionExist = useCallback(() => {
    memberCenterService.checkRedeemGiftPromotionExist().then((res) => {
      setIsPromotionExist(res.data.result);
    });
  }, []);

  const onLoadMore = useCallback(() => {
    if (loading) {
      return;
    }
    fetchMemberPoints(pagination.page + 1);
  }, [fetchMemberPoints, loading, pagination.page]);

  useEffect(() => {
    fetchMemberPoints();
    fetchRules();
    fetchFulfillmentSummary();
    checkRedeemGiftPromotionExist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const coloums = useMemo(() => {
    const coloum_map = {
      created_at: {
        id: 'created_at',
        key: 'created_at',
        title: t('member_points.fields.date'),
        width: '16.667%',
      },
      remarks: {
        id: 'remarks',
        key: 'remarks',
        title: t('member_points.fields.remarks'),
        width: '33.333%',
        mobileShow: false,
      },
      value: {
        id: 'value',
        key: 'value',
        title: t('member_points.fields.value'),
        width: '16.6667%',
        mobileShow: false,
      },
      expire_at: {
        id: 'expire_at',
        key: 'expire_at',
        title: t('member_points.fields.expiry_date'),
        width: '16.6667%',
      },
      point_balance: {
        id: 'point_balance',
        key: 'point_balance',
        title: t('member_points.point_balance'),
      },
    };

    const baseColumns = [
      [coloum_map.remarks, coloum_map.value],
      coloum_map.created_at,
      coloum_map.remarks,
      coloum_map.value,
      coloum_map.expire_at,
    ];

    return hideCreditPointRecordBalance
      ? baseColumns
      : [...baseColumns, coloum_map.point_balance];
  }, [t, hideCreditPointRecordBalance]);

  const onRuleModalOpen = useCallback(() => {
    if (rules && !isRuleModalOpen) {
      setIsRuleModalOpen(true);
    }
  }, [isRuleModalOpen, rules]);

  const onRuleModalClose = useCallback(() => {
    setIsRuleModalOpen(false);
  }, []);

  return (
    <TabContent url={getTabUrl(TAB_TYPE.POINTS)}>
      <BlockTitle
        showSkeleton={!summaryInfo}
        title={t('member_points.point_history')}
      />
      <Board showSkeleton={!summaryInfo}>
        <div className="member-center__board-block split" key="l">
          <div className="member-center__board-left">
            <span className="member-center__board-left-title">
              {t('member_points.point_balance')}
            </span>
            <span className="member-center__board-left-value-container">
              <span className="num">
                {currentUser.member_point_balance || 0}
              </span>
              <span>{t('member_points.unit')}</span>
            </span>
            {isRedeemOrderActive && pointEqualCash && (
              <span className="member-center__board-left-tag">
                {t('member_points.equal_cash', { money: pointEqualCash })}
              </span>
            )}
          </div>
        </div>
        <div className="member-center__board-block grow" key="m">
          <div className="member-center__board-middle">
            {shouldShowNextExpiryPoints &&
              summaryInfo.items.map((item) => {
                return (
                  <div key={`${item.total_value}-${item.expire_at}`}>
                    <Text as="span" fontWeight="bold">
                      {item.total_value}&nbsp;
                    </Text>
                    <span>
                      {t('member_points.display.expire_point')}
                      &nbsp;
                      {dayjs(item.expire_at).format('YYYY-MM-DD')}
                      &nbsp;
                      {t('member_points.fields.expire')}
                    </span>
                  </div>
                );
              })}
            {shouldShowNeverExpirePoints && (
              <div>
                {summaryInfo.never_expire_item.total_value}&nbsp;
                <span>{t('store_credits.fields.never_expires_point')}</span>
              </div>
            )}
            {rulesDescription && <div>{rulesDescription}</div>}
            <div
              className="member-center__board-middle-entry"
              onClick={onRuleModalOpen}
              onKeyUp={onRuleModalOpen}
              role="button"
              tabIndex={0}
            >
              {t('member_points.rule')}
            </div>
          </div>
        </div>
        {isPromotionExist && currentUser.member_point_balance > 0 && (
          <div className="member-center__board-block" key="r">
            <div className="member-center__board-right">
              <a href="/promotions/redeem_gift">
                {t('member_points.display.redeem_btn')}
              </a>
            </div>
          </div>
        )}
      </Board>
      <List
        listKey="_id"
        list={points}
        coloums={coloums}
        emptyProps={{
          description: t('member_point.list.empty.description'),
          buttonProps: {
            text: t('member_center.list.empty.buttonText'),
            link: '/products',
          },
        }}
        showSkeleton={!points}
        skeleton={{
          rows: 6,
          mobileRows: 3,
          mobileSizes: [
            [43.5, 9.2],
            [15.24, 33.65],
            [22.22, 26.35],
            [29.21, 13.02],
          ],
        }}
        paginationProps={{
          pagination: pagination,
          loading: points?.length > 0 && loading,
          onLoadMore,
        }}
        RenderComponent={TableRenderComponent}
      />
      <RulesRemarkModal
        content={ruleRemark}
        isOpen={isRuleModalOpen}
        onClose={onRuleModalClose}
      />
    </TabContent>
  );
};

Points.propTypes = {};

export default Points;
